import { z } from 'zod';

/** Long enough for a legal account name in either script. */
export const PAYMENT_PARTY_MAX_LENGTH = 200;

/** An IBAN is at most 34 characters; the slack absorbs an operator's spacing. */
export const PAYMENT_ACCOUNT_MAX_LENGTH = 64;

/**
 * Where a member sends money when they pay outside the stores (P23c1).
 *
 * ## Why this is a structured value and not a block of prose
 *
 * The obvious shape is a `textarea` of instructions, and it is the one shape
 * this platform cannot have: `textarea` was removed from `SETTING_CONTROLS` in
 * P10.3a — *"no setting is prose"* — and `json` is the blob `defineSetting`
 * refuses outright without an owning module (U0). Payment coordinates are not
 * prose anyway; they are four named facts, which is exactly the `object`
 * control's domain and exactly what `brand.legalEntity` already is.
 *
 * The consequence is the good one: an operator fills four labelled boxes instead
 * of composing a paragraph, and the client can lay the facts out as a copyable
 * list rather than printing somebody's line breaks into a phone screen.
 *
 * ## It is NOT translatable, for `brand.legalEntity`'s own reason
 *
 * An IBAN has one spelling, and a bank's registered name is the bank's. A
 * per-locale copy of either would let a mistranslation send money somewhere
 * else. The *sentences* around these values are a different thing entirely, and
 * they live where prose belongs — the client's ar/en catalogues (U8).
 *
 * ## Blank is a legitimate value
 *
 * A deployment that takes cash at an office, or that sells only through the
 * stores, leaves these empty. That is why the fields are plain strings with no
 * `min(1)`: the *screen* decides what an empty set means (it falls back to the
 * brand's support contact), and a required field here would force an operator to
 * invent a bank account in order to save an unrelated setting.
 */
export const DirectPaymentDetailsSchema = z
  .object({
    /** The account's legal holder — usually the brand's operating company. */
    accountHolder: z.string().max(PAYMENT_PARTY_MAX_LENGTH),
    bankName: z.string().max(PAYMENT_PARTY_MAX_LENGTH),
    iban: z.string().max(PAYMENT_ACCOUNT_MAX_LENGTH),
    /** For markets that quote a local account number rather than an IBAN. */
    accountNumber: z.string().max(PAYMENT_ACCOUNT_MAX_LENGTH),
  })
  .strict();

export type DirectPaymentDetails = z.infer<typeof DirectPaymentDetailsSchema>;

/** The coded default — see the docblock on why "unset" is a real state. */
export const EMPTY_DIRECT_PAYMENT_DETAILS: DirectPaymentDetails = {
  accountHolder: '',
  bankName: '',
  iban: '',
  accountNumber: '',
};

/**
 * Whether these details actually tell a member where to send money.
 *
 * Deliberately **not** "is any field filled". A holder and a bank name say who
 * and where but never *which account*, so a panel drawn on those alone asks a
 * member to make a transfer they cannot complete — worse than saying nothing and
 * pointing them at support. The destination is the IBAN or the account number,
 * and one of the two is enough.
 *
 * Trimmed, because a value typed as a space is the same nothing as an empty box
 * and no operator would tell the two apart.
 */
export function hasPaymentCoordinates(details: DirectPaymentDetails): boolean {
  return details.iban.trim() !== '' || details.accountNumber.trim() !== '';
}
